/**
 * View screen (§12.1, PRD §2).
 *
 * One screen, four modes. The route contract is fixed by the shell (`/view/:t/:id?mode=`); this
 * file owns everything under it:
 *
 * - loads the per-subject UI state **before** any data query runs (§12.4) and gates the mode
 *   queries on it, so a persisted filter is never briefly ignored;
 * - resolves the range preset into `from`/`to` and builds the §5.2 filter params once;
 * - routes the empty states from `GET /api/views/:t/:id/summary` (#23) — `neverScraped` gets the
 *   "Scrape now" call to action the PRD asks for;
 * - hosts the post preview popup for every mode.
 *
 * Layout (redesign): every mode renders in the centered 960px column except Posts, which is
 * edge-to-edge fullscreen — the grid scrolls, the document does not. The counted-post-types list
 * lives in the Metrics header (`CountedTypesMenu`), not in Settings.
 *
 * The Settings modal is mounted here again for Save to Notion: the feed card and the preview popup
 * both need a way into Settings → Notion, and they reach it through this one piece of view-level
 * state (SPEC § Setup modal).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type {
  BucketGranularity,
  ScheduleHeatmapMode,
  ViewFiltersState,
  ViewMode,
  ViewRangeState,
} from '@lw/shared';
import { getViewSummary } from '../../api/views';
import { QK } from '../../api/queryKeys';
import { pageTitle } from '../../app/appTitle';
import { HOME_PATH, VIEW_MODE_PARAM } from '../../app/routes';
import { useViewRoute } from '../../app/useViewRoute';
import { EmptyState, ErrorState, Spinner, cx } from '../../components';
import { useDebouncedCallback, useDocumentTitle } from '../../lib/hooks';
import { VIEW_MODE_LABEL } from '../../lib/labels';
import {
  useAppTimezone,
  useMediaEnabled,
  useSettingsQuery,
  useTimezoneBootstrap,
} from '../settings/useSettings';
import { SettingsModal, type SettingsTab } from '../settings/SettingsModal';
import { NotionSaveProvider } from '../notion/useNotionSave';
import { useStartScrape } from '../home/useHomeData';
import { FeedMode } from './feed/FeedMode';
import { MetricsMode } from './metrics/MetricsMode';
import { PostsMode } from './posts/PostsMode';
import { ProfileMode } from './profile/ProfileMode';
import { PostPreviewProvider } from './PreviewContext';
import { ViewHeader } from './ViewHeader';
import { useViewUiState } from './useViewUiState';
import {
  anchorNow,
  hasAnyFilter,
  resolveRange,
  toFilterParams,
  type ViewSort,
} from './viewState';
import './view.css';

const SEARCH_DEBOUNCE_MS = 400;

export function ViewPage() {
  const { subjectType, id, mode, setMode } = useViewRoute();

  if (subjectType === null || id === null) return <Navigate to={HOME_PATH} replace />;

  // Remounting on subject change keeps every per-subject hook (UI state, queries, scroll) honest.
  return <ViewScreen key={`${subjectType}:${id}`} subjectType={subjectType} id={id} mode={mode} setMode={setMode} />;
}

type ViewScreenProps = {
  subjectType: 'profile' | 'group';
  id: number;
  mode: ViewMode;
  setMode: (mode: ViewMode) => void;
};

function ViewScreen({ subjectType, id, mode, setMode }: ViewScreenProps) {
  useTimezoneBootstrap();

  const timezone = useAppTimezone();
  const mediaEnabled = useMediaEnabled();
  const settings = useSettingsQuery();
  const countedPostTypes = settings.data?.countedPostTypes ?? [];

  const uiState = useViewUiState(subjectType, id);
  const [searchParams] = useSearchParams();
  const startScrape = useStartScrape();

  // One piece of state for both Save-to-Notion surfaces (§12.1): the setup modal's CTA and an
  // auth-flavoured save error both open Settings here.
  const [settingsTab, setSettingsTab] = useState<SettingsTab | null>(null);
  const openNotionSettings = useCallback(() => {
    setSettingsTab('notion');
  }, []);

  const isGroup = subjectType === 'group';

  /* ------------------------------------------------------------ mode sync */

  const modeRestored = useRef(false);
  useEffect(() => {
    if (!uiState.ready || modeRestored.current) return;
    modeRestored.current = true;
    const stored = uiState.state.mode;
    // The URL wins when it names a mode explicitly; otherwise the persisted mode is restored.
    if (stored !== undefined && stored !== mode && !searchParams.has(VIEW_MODE_PARAM)) {
      setMode(stored);
    }
  }, [uiState.ready, uiState.state.mode, mode, searchParams, setMode]);

  useEffect(() => {
    if (!uiState.ready || !modeRestored.current) return;
    if (uiState.state.mode !== mode) uiState.update({ mode });
  }, [mode, uiState]);

  /* ----------------------------------------------------------- range/params */

  // Re-anchored whenever the range selection changes; stable within a minute otherwise, so query
  // keys do not churn on every render.
  const rangeKey = `${uiState.state.range.preset}|${uiState.state.range.from ?? ''}|${uiState.state.range.to ?? ''}`;
  const now = useMemo(() => anchorNow(), [rangeKey]);
  const resolvedRange = useMemo(() => resolveRange(uiState.state.range, now), [rangeKey, now]);

  const filtersKey = JSON.stringify(uiState.state.filters);
  const baseParams = useMemo(
    () => toFilterParams(uiState.state.filters, resolvedRange, { isGroup }),
    [filtersKey, resolvedRange, isGroup],
  );
  const postsParams = useMemo(
    () => toFilterParams(uiState.state.filters, resolvedRange, { isGroup, withQuery: true }),
    [filtersKey, resolvedRange, isGroup],
  );

  /* -------------------------------------------------------------- summary */

  const summaryQuery = useQuery({
    queryKey: QK.viewSummary(subjectType, id),
    queryFn: ({ signal }) => getViewSummary(subjectType, id, signal),
  });
  const summary = summaryQuery.data;

  // `<name> — Feed · LinkedIn Watcher`. `summary` is undefined while pending *and* on error, and
  // both cases have no name to show, so the tab falls back to the bare app name. `mode` is a prop
  // and `ViewScreen` is remounted per subject, so this re-runs on either change by itself.
  useDocumentTitle(
    pageTitle(summary === undefined ? undefined : `${summary.name} — ${VIEW_MODE_LABEL[mode]}`),
  );

  /* --------------------------------------------------------------- search */

  const [searchDraft, setSearchDraft] = useState<string | null>(null);
  const commitSearch = useDebouncedCallback((value: string) => {
    const filters: ViewFiltersState = { ...uiState.state.filters };
    if (value.trim().length === 0) delete filters.q;
    else filters.q = value.slice(0, 200);
    uiState.update({ filters });
  }, SEARCH_DEBOUNCE_MS);

  const search = searchDraft ?? uiState.state.filters.q ?? '';

  /* ---------------------------------------------------------------- render */

  const setFilters = (filters: ViewFiltersState) => uiState.update({ filters });
  const setRange = (range: ViewRangeState) => uiState.update({ range });
  const setSort = (sort: ViewSort) => uiState.update({ sort });
  const setFeedSort = (feedSort: ViewSort) => uiState.update({ feedSort });
  const clearFilters = () => {
    setSearchDraft('');
    uiState.update({ filters: {} });
  };

  const scrapeNow = () => {
    if (isGroup) startScrape.mutate({ groupIds: [id] });
    else startScrape.mutate({ profileIds: [id] });
  };

  const queriesEnabled = uiState.ready && summaryQuery.isSuccess;
  const neverScraped = summary?.neverScraped === true;

  const showStates =
    summaryQuery.isError ||
    uiState.error !== null ||
    summaryQuery.isPending ||
    !uiState.ready ||
    (queriesEnabled && neverScraped);

  return (
    <NotionSaveProvider onOpenSettings={openNotionSettings}>
      <PostPreviewProvider mediaEnabled={mediaEnabled} timezone={timezone}>
        <div className={cx('view', mode === 'posts' && 'view--posts')}>
          <ViewHeader
            subjectType={subjectType}
            id={id}
            summary={summary}
            mode={mode}
            onModeChange={setMode}
            filters={uiState.state.filters}
            onFiltersChange={setFilters}
            range={uiState.state.range}
            resolvedRange={resolvedRange}
            onRangeChange={setRange}
            // The header gets the sort of the mode it is rendering — Posts and Feed keep their own
            // (§12.3). Safe for the Export links inside it: they mount only in posts mode, where
            // the resolved sort *is* the posts sort.
            sort={mode === 'feed' ? uiState.state.feedSort : uiState.state.sort}
            onSortChange={mode === 'feed' ? setFeedSort : setSort}
            countedPostTypes={countedPostTypes}
            mediaEnabled={mediaEnabled}
            timezone={timezone}
            now={now}
            search={search}
            onSearchChange={(value) => {
              setSearchDraft(value);
              commitSearch(value);
            }}
            postsParams={postsParams}
          />

          {showStates ? (
            <div className="page view-body">
              <div className="content">
                {summaryQuery.isError ? (
                  <ErrorState
                    title="Couldn't load this view"
                    error={summaryQuery.error}
                    onRetry={() => void summaryQuery.refetch()}
                    retrying={summaryQuery.isFetching}
                  />
                ) : null}

                {uiState.error !== null ? (
                  <ErrorState
                    title="Couldn't restore your saved filters"
                    description="The view is showing the default range and filters. Retrying re-reads what was stored."
                    onRetry={uiState.retry}
                  />
                ) : null}

                {summaryQuery.isPending || !uiState.ready ? (
                  <Spinner size="lg" label="Loading view…" />
                ) : null}

                {queriesEnabled && neverScraped ? (
                  <EmptyState
                    title="Never scraped"
                    description={
                      isGroup
                        ? 'No member of this group has been scraped yet. Start a scrape to fill in metrics, posts, feed and profiles.'
                        : 'This profile has not been scraped yet. Start a scrape to collect its posts and profile snapshot.'
                    }
                    action={
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={scrapeNow}
                        disabled={startScrape.isPending}
                      >
                        {startScrape.isPending ? 'Queuing…' : 'Scrape now'}
                      </button>
                    }
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {queriesEnabled && !neverScraped ? (
            mode === 'posts' ? (
              <PostsMode
                subjectType={subjectType}
                id={id}
                params={postsParams}
                sort={uiState.state.sort}
                onSortChange={setSort}
                enabled={queriesEnabled}
                timezone={timezone}
                mediaEnabled={mediaEnabled}
                hasFilters={hasAnyFilter(uiState.state.filters, isGroup, true)}
                onClearFilters={clearFilters}
                summary={summary}
              />
            ) : (
              <div className="page view-body">
                <div className="content">
                  {mode === 'metrics' ? (
                    <MetricsMode
                      subjectType={subjectType}
                      id={id}
                      params={baseParams}
                      enabled={queriesEnabled}
                      timezone={timezone}
                      granularity={uiState.state.granularity}
                      onGranularityChange={(g: BucketGranularity) => uiState.update({ granularity: g })}
                      calendarMode={uiState.state.calendarMode}
                      matrixMode={uiState.state.matrixMode}
                      onCalendarModeChange={(m: ScheduleHeatmapMode) =>
                        uiState.update({ calendarMode: m })
                      }
                      onMatrixModeChange={(m: ScheduleHeatmapMode) => uiState.update({ matrixMode: m })}
                      filters={uiState.state.filters}
                      onFiltersChange={setFilters}
                    />
                  ) : null}

                  {mode === 'feed' ? (
                    <FeedMode
                      subjectType={subjectType}
                      id={id}
                      params={baseParams}
                      sort={uiState.state.feedSort}
                      enabled={queriesEnabled}
                      mediaEnabled={mediaEnabled}
                      hasFilters={hasAnyFilter(uiState.state.filters, isGroup, false)}
                      onClearFilters={clearFilters}
                    />
                  ) : null}

                  {mode === 'profile' ? (
                    <ProfileMode
                      subjectType={subjectType}
                      id={id}
                      enabled={queriesEnabled}
                      mediaEnabled={mediaEnabled}
                      onScrapeNow={scrapeNow}
                    />
                  ) : null}
                </div>
              </div>
            )
          ) : null}

          <SettingsModal
            open={settingsTab !== null}
            initialTab={settingsTab ?? 'notion'}
            onClose={() => {
              setSettingsTab(null);
            }}
          />
        </div>
      </PostPreviewProvider>
    </NotionSaveProvider>
  );
}

export default ViewPage;
